import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import "../style/cart_page.css";
import {
  clearCart,
  getCartSummary,
  loadCart,
  removeFromCart,
  saveCart,
  updateCartQuantity,
} from "../services/cartService";

export default function CartPage() {
  const [cart, setCart] = useState([]);

  useEffect(() => {
    const syncFromStorage = () => {
      setCart(loadCart());
    };

    syncFromStorage();
    window.addEventListener("storage", syncFromStorage);

    return () => {
      window.removeEventListener("storage", syncFromStorage);
    };
  }, []);

  const syncCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem("cartSummary", JSON.stringify(getCartSummary(updatedCart)));
  };

  const handleRemoveItem = (id) => {
    syncCart(removeFromCart(id));
  };

  const handleUpdateQuantity = (id, delta) => {
    syncCart(updateCartQuantity(id, delta));
  };

  const handleClearCart = () => {
    clearCart();
    syncCart([]);
    toast.info("Your shopping cart has been emptied.");
  };

  const proceedToCheckout = () => {
    localStorage.setItem("cartSummary", JSON.stringify(getCartSummary(cart)));
    saveCart(cart);
  };

  const summary = getCartSummary(cart);

  return (
    <div className="cart-page-wrapper">
      <nav className="breadcrumb-nav">
        <NavLink to="/home" className="breadcrumb-item">Home</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <NavLink to="/product" className="breadcrumb-item">Product Dashboard</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <span className="breadcrumb-item active-page">Cart</span>
      </nav>

      <main className="cart-page-container">
        <section className="cart-hero">
          <div>
            <p className="cart-eyebrow">Checkout Step 1</p>
            <h1>Shopping Cart ({summary.items} items)</h1>
            <p className="cart-copy">
              Review products, adjust quantity, and continue to secure checkout.
            </p>
          </div>
          <div className="cart-step-pill">Cart - Address - Payment - Success</div>
        </section>

        <div className="cart-content-wrapper">
          <div className="cart-items-list">
            {cart.length === 0 ? (
              <div className="empty-cart-message">
                <h2>Your cart is empty</h2>
                <p>Add products from the storefront or flash sale to continue the ecommerce flow.</p>
                <NavLink to="/product" className="checkout-link-btn">
                  Browse Products
                </NavLink>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.id} className="cart-item">
                  <img
                    src={item.url || item.image || "https://via.placeholder.com/100"}
                    alt={item.name}
                    className="cart-item-image"
                    loading="lazy"
                    decoding="async"
                  />

                  <div className="cart-item-details">
                    <h3>{item.name}</h3>
                    <p className="price-per-item">{item.brand ? `${item.brand} · ${item.category}` : item.category}</p>
                    {item.description ? <p className="price-per-item">{item.description}</p> : null}
                    <p className="cart-item-price">
                      Rs. {Number(item.price).toLocaleString("en-IN")}
                    </p>
                    {Array.isArray(item.highlights) && item.highlights.length ? (
                      <div className="cart-item-highlights">
                        {item.highlights.slice(0, 2).map((highlight) => (
                          <span key={highlight}>{highlight}</span>
                        ))}
                      </div>
                    ) : null}

                    <div className="cart-item-quantity">
                      <button className="quantity-btn" onClick={() => handleUpdateQuantity(item.id, -1)}>
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button className="quantity-btn" onClick={() => handleUpdateQuantity(item.id, 1)}>
                        +
                      </button>

                      <span className="cart-item-subtotal">
                        Subtotal: Rs.
                        {(Number(item.price) * Number(item.quantity)).toLocaleString("en-IN")}
                      </span>
                    </div>

                    <button className="remove-btn" onClick={() => handleRemoveItem(item.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}

            {cart.length > 0 && (
              <button
                onClick={handleClearCart}
                className="checkout-link-btn"
                style={{ background: "#0f172a", marginTop: "0" }}
              >
                Clear Cart
              </button>
            )}
          </div>

          <aside className="cart-summary-panel">
            <h2>Price Details</h2>

            <div className="summary-line">
              <span>Price ({summary.items} items)</span>
              <span>Rs.{summary.subtotal.toLocaleString("en-IN")}</span>
            </div>

            <div className="summary-line">
              <span>Discount</span>
              <span className="discount-color">- Rs.{summary.discount.toLocaleString("en-IN")}</span>
            </div>

            <div className="summary-line">
              <span>Delivery Charges</span>
              <span className="delivery-color">FREE</span>
            </div>

            <hr />

            <div className="summary-line total-payable">
              <span>Total Payable</span>
              <span>Rs.{summary.totalPayable.toLocaleString("en-IN")}</span>
            </div>

            <NavLink to="/address" className="checkout-link-btn" onClick={proceedToCheckout}>
              Proceed to Checkout
            </NavLink>
            <NavLink to="/product" className="ghost-link-btn">
              Continue Shopping
            </NavLink>
          </aside>
        </div>
      </main>
    </div>
  );
}
